import { useDataProvider, useGetIdentity, type DataProvider } from "ra-core";
import { useCallback, useMemo } from "react";

import type { Company, Tag } from "../types";
import {
  canonicalFitConfidence,
  canonicalFitQuality,
  canonicalIcpProfile,
  canonicalRelationshipType,
  canonicalBuyerStatus,
} from "../root/crmVocabulary.generated";

export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  company: string;
  email_work: string;
  email_home: string;
  email_other: string;
  phone_work: string;
  phone_home: string;
  phone_other: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  has_newsletter: string;
  status: string;
  tags: string;
  linkedin_url: string;
  connection_pipeline_stage: string;
  relationship_motion: string;
  next_touch_date: string;
  next_touch_status: string;
  goextrovert_prospect_id: string;
  campaign_name: string;
  group: string;
  relationship_strength: string;
  last_meaningful_interaction_at: string;
  relationship_role: string;
  influence?: string;
  relationship_evidence: string;
  next_relationship_move: string;
  city: string;
  region: string;
  country: string;
  location_source: string;
  location_verified_at: string;
  location_confidence: string;
  fit_quality?: string;
  icp_profile?: string;
  /** Separated by ";" or ",": the export writes "; ", both are accepted back. */
  relationship_types?: string;
  seniority?: string;
  buyer_status?: string;
  fit_confidence?: string;
  fit_rationale?: string;
  fit_hook?: string;
  fit_missing_evidence?: string;
  Seniority?: string;
};

export function useContactImport() {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  // company cache to avoid creating the same company multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  const companiesCache = useMemo(
    () => new Map<string, Company>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );
  const getCompanies = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Company>(
        "companies",
        companiesCache,
        names,
        (name) => ({
          name,
          created_at: new Date().toISOString(),
          sales_id: user?.identity?.id,
        }),
        dataProvider,
      ),
    [companiesCache, user?.identity?.id, dataProvider],
  );

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);
  const getTags = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        (name) => ({
          name,
          color: "#f9f9f9",
        }),
        dataProvider,
      ),
    [tagsCache, dataProvider],
  );

  const processBatch = useCallback(
    async (batch: ContactImportSchema[]) => {
      const [companies, tags] = await Promise.all([
        getCompanies(
          batch
            .map((contact) => contact.company?.trim())
            .filter((name) => name),
        ),
        getTags(batch.flatMap((batch) => parseTags(batch.tags))),
      ]);

      await Promise.all(
        batch.map(
          async ({
            first_name,
            last_name,
            gender,
            title,
            email_work,
            email_home,
            email_other,
            phone_work,
            phone_home,
            phone_other,
            background,
            first_seen,
            last_seen,
            has_newsletter,
            status,
            company: companyName,
            tags: tagNames,
            linkedin_url,
            connection_pipeline_stage,
            relationship_motion,
            next_touch_date,
            next_touch_status,
            goextrovert_prospect_id,
            campaign_name,
            group,
            relationship_strength,
            last_meaningful_interaction_at,
            relationship_role,
            influence,
            relationship_evidence,
            next_relationship_move,
            city,
            region,
            country,
            location_source,
            location_verified_at,
            location_confidence,
            fit_quality,
            icp_profile,
            relationship_types,
            seniority,
            buyer_status,
            fit_confidence,
            fit_rationale,
            fit_hook,
            fit_missing_evidence,
            Seniority,
          }) => {
            const email_jsonb = [
              { email: email_work, type: "Work" },
              { email: email_home, type: "Home" },
              { email: email_other, type: "Other" },
            ].filter(({ email }) => email);
            const phone_jsonb = [
              { number: phone_work, type: "Work" },
              { number: phone_home, type: "Home" },
              { number: phone_other, type: "Other" },
            ].filter(({ number }) => number);
            const company = companyName?.trim()
              ? companies.get(companyName.trim())
              : undefined;
            const tagList = parseTags(tagNames)
              .map((name) => tags.get(name))
              .filter((tag): tag is Tag => !!tag);

            const resolvedStatus = relationship_strength
              ? relationship_strength === "Active" ||
                relationship_strength === "Trusted"
                ? "A"
                : relationship_strength === "Warm" ||
                    relationship_strength === "Tepid"
                  ? "B"
                  : "C"
              : status;

            // The same three-way signal has arrived under four column names
            // over the years — `influence`, `relationship_role`, the LinkedIn
            // sheet's `Seniority`, and now `buyer_status`. All of them land in
            // `buyer_status`, which is the column buyer_proximity derives from;
            // `influence` is no longer written, because it is exactly this
            // signal in four incompatible encodings that made it unusable.
            const rawRole = (
              buyer_status ||
              seniority ||
              influence ||
              relationship_role ||
              Seniority ||
              ""
            ).trim();
            const resolvedRole =
              rawRole === "Decision Maker" ||
              rawRole === "A" ||
              rawRole.startsWith("1")
                ? "decision maker"
                : rawRole === "Influencer" ||
                    rawRole === "B" ||
                    rawRole.startsWith("2")
                  ? "influencer"
                  : rawRole === "Everyone Else" ||
                      rawRole === "C" ||
                      rawRole.startsWith("3") ||
                      rawRole === "Neither"
                    ? "everyone else"
                    : (canonicalBuyerStatus(rawRole) ?? undefined);
            // A value outside the catalog would be rejected by the
            // enforce_relationship_vocabulary trigger and fail the whole row,
            // so an unrecognised import value is dropped rather than guessed.
            const resolvedFitQuality =
              canonicalFitQuality(fit_quality) ?? undefined;
            const resolvedIcpProfile =
              canonicalIcpProfile(icp_profile) ?? undefined;
            const resolvedFitConfidence =
              canonicalFitConfidence(fit_confidence) ?? undefined;
            // Every member has to canonicalize, and a member that does not is
            // dropped rather than failing the row — same rule as the scalars.
            // An explicitly empty cell is NOT the same as an absent column: it
            // is the answer "no role", so it imports as the empty array while
            // an absent column leaves the contact unasked.
            const parsedRelationshipTypes =
              relationship_types === undefined
                ? undefined
                : relationship_types
                    .split(/[;,]/)
                    .map((part) => canonicalRelationshipType(part))
                    .filter((value): value is string => value != null);
            const resolvedRelationshipTypes =
              parsedRelationshipTypes === undefined
                ? undefined
                : Array.from(new Set(parsedRelationshipTypes));
            const normalizedLocationConfidence = location_confidence
              ?.trim()
              .toLowerCase();
            const resolvedLocationConfidence =
              normalizedLocationConfidence === "high" ||
              normalizedLocationConfidence === "medium" ||
              normalizedLocationConfidence === "low"
                ? normalizedLocationConfidence
                : undefined;

            return dataProvider.create("contacts", {
              data: {
                first_name,
                last_name,
                gender,
                title,
                email_jsonb,
                phone_jsonb,
                background,
                first_seen: first_seen
                  ? new Date(first_seen).toISOString()
                  : today,
                last_seen: last_seen
                  ? new Date(last_seen).toISOString()
                  : today,
                has_newsletter,
                status: resolvedStatus || "C",
                company_id: company?.id,
                tags: tagList.map((tag) => tag.id),
                sales_id: user?.identity?.id,
                linkedin_url,
                connection_pipeline_stage:
                  connection_pipeline_stage || undefined,
                relationship_motion: relationship_motion || undefined,
                next_touch_date: next_touch_date
                  ? new Date(next_touch_date).toISOString()
                  : undefined,
                next_touch_status: next_touch_status || undefined,
                goextrovert_prospect_id: goextrovert_prospect_id || undefined,
                campaign_name: campaign_name || undefined,
                group: group || undefined,
                last_meaningful_interaction_at: last_meaningful_interaction_at
                  ? new Date(last_meaningful_interaction_at).toISOString()
                  : undefined,
                relationship_evidence: relationship_evidence || undefined,
                next_relationship_move: next_relationship_move || undefined,
                city: city?.trim() || undefined,
                region: region?.trim() || undefined,
                country: country?.trim() || undefined,
                location_source: location_source?.trim() || undefined,
                location_verified_at: location_verified_at
                  ? new Date(location_verified_at).toISOString()
                  : undefined,
                location_confidence: resolvedLocationConfidence,
                fit_quality: resolvedFitQuality,
                icp_profile: resolvedIcpProfile,
                relationship_types: resolvedRelationshipTypes,
                buyer_status: resolvedRole,
                fit_confidence: resolvedFitConfidence,
                fit_rationale: fit_rationale?.trim() || undefined,
                fit_hook: fit_hook?.trim() || undefined,
                fit_missing_evidence: fit_missing_evidence?.trim() || undefined,
              },
            });
          },
        ),
      );
    },
    [dataProvider, getCompanies, getTags, user?.identity?.id, today],
  );

  return processBatch;
}

const fetchRecordsWithCache = async function <T>(
  resource: string,
  cache: Map<string, T>,
  names: string[],
  getCreateData: (name: string) => Partial<T>,
  dataProvider: DataProvider,
) {
  const trimmedNames = [...new Set(names.map((name) => name.trim()))];
  const uncachedRecordNames = trimmedNames.filter((name) => !cache.has(name));

  // check the backend for existing records
  if (uncachedRecordNames.length > 0) {
    const response = await dataProvider.getList(resource, {
      filter: {
        "name@in": `(${uncachedRecordNames
          .map((name) => `"${name}"`)
          .join(",")})`,
      },
      pagination: { page: 1, perPage: trimmedNames.length },
      sort: { field: "id", order: "ASC" },
    });
    for (const record of response.data) {
      cache.set(record.name.trim(), record);
    }
  }

  // create missing records in parallel
  await Promise.all(
    uncachedRecordNames.map(async (name) => {
      if (cache.has(name)) return;
      const response = await dataProvider.create(resource, {
        data: getCreateData(name),
      });
      cache.set(name, response.data);
    }),
  );

  // now all records are in cache, return a map of all records
  return trimmedNames.reduce((acc, name) => {
    acc.set(name, cache.get(name) as T);
    return acc;
  }, new Map<string, T>());
};

const parseTags = (tags: string) =>
  tags
    ?.split(",")
    ?.map((tag: string) => tag.trim())
    ?.filter((tag: string) => tag) ?? [];
